import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Plus, Upload, Ticket, Calendar, Euro, Pencil, Trash2, AlertTriangle, BarChart3, FileX2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime, formatPrice } from '../../lib/utils';

const AdminTicketSlots = () => {
  const [slots, setSlots] = useState([]);
  const [monuments, setMonuments] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [filterMonument, setFilterMonument] = useState('all');
  const fileInputRef = useRef(null);
  
  const [slotForm, setSlotForm] = useState({
    monument_id: '',
    slot_start_at: '',
    slot_end_at: '',
    category_label: 'adult',
    is_active: true,
  });

  // Updated pricing form to include both prices
  const [pricingForm, setPricingForm] = useState({
    slot_id: '',
    member_price: '',
    external_price: '',
  });

  const [uploadFiles, setUploadFiles] = useState([]);
  const [savingPricing, setSavingPricing] = useState(false);
  
  // Edit and Delete state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clearFilesDialogOpen, setClearFilesDialogOpen] = useState(false);
  const [slotStats, setSlotStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clearingFiles, setClearingFiles] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [editForm, setEditForm] = useState({
    slot_start_at: '',
    slot_end_at: '',
    category_label: '',
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, [filterMonument]);

  const loadData = async () => {
    try {
      const [slotsRes, monumentsRes, pricingRes] = await Promise.all([
        adminAPI.listTicketSlots(filterMonument !== 'all' ? { monument_id: filterMonument } : {}),
        adminAPI.listMonuments(),
        adminAPI.listPricingRules(),
      ]);
      setSlots(slotsRes.data);
      setMonuments(monumentsRes.data);
      setPricingRules(pricingRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get pricing for a slot
  const getSlotPricing = (slotId) => {
    const memberPrice = pricingRules.find(p => p.slot_id === slotId && p.user_type === 'member');
    const externalPrice = pricingRules.find(p => p.slot_id === slotId && p.user_type === 'external');
    return {
      member: memberPrice?.price_eur_cents,
      external: externalPrice?.price_eur_cents,
    };
  };

  const handleCreateSlot = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createTicketSlot({
        ...slotForm,
        slot_start_at: new Date(slotForm.slot_start_at).toISOString(),
        slot_end_at: slotForm.slot_end_at ? new Date(slotForm.slot_end_at).toISOString() : null,
      });
      toast.success('Slot created successfully');
      setSlotDialogOpen(false);
      setSlotForm({
        monument_id: '',
        slot_start_at: '',
        slot_end_at: '',
        category_label: 'adult',
        is_active: true,
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create slot');
    }
  };

  const handleUploadTickets = async (e) => {
    e.preventDefault();
    if (!selectedSlot || uploadFiles.length === 0) return;
    
    try {
      await adminAPI.uploadTicketFiles(selectedSlot.id, uploadFiles);
      toast.success(`Uploaded ${uploadFiles.length} ticket files`);
      setUploadDialogOpen(false);
      setUploadFiles([]);
      setSelectedSlot(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload tickets');
    }
  };

  // Updated to save both member and external prices
  const handleSavePricing = async (e) => {
    e.preventDefault();
    setSavingPricing(true);
    
    try {
      const slotId = pricingForm.slot_id;
      const existingPricing = getSlotPricing(slotId);
      
      // Delete existing pricing rules for this slot
      const existingRules = pricingRules.filter(p => p.slot_id === slotId);
      for (const rule of existingRules) {
        await adminAPI.deletePricingRule(rule.id);
      }
      
      // Create member pricing if provided
      if (pricingForm.member_price) {
        await adminAPI.createPricingRule({
          slot_id: slotId,
          user_type: 'member',
          price_eur_cents: Math.round(parseFloat(pricingForm.member_price) * 100),
        });
      }
      
      // Create external pricing if provided
      if (pricingForm.external_price) {
        await adminAPI.createPricingRule({
          slot_id: slotId,
          user_type: 'external',
          price_eur_cents: Math.round(parseFloat(pricingForm.external_price) * 100),
        });
      }
      
      toast.success('Pricing saved successfully');
      setPricingDialogOpen(false);
      setPricingForm({ slot_id: '', member_price: '', external_price: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const openUploadDialog = (slot) => {
    setSelectedSlot(slot);
    setUploadDialogOpen(true);
  };

  const openPricingDialog = (slot) => {
    const pricing = getSlotPricing(slot.id);
    setPricingForm({
      slot_id: slot.id,
      member_price: pricing.member ? (pricing.member / 100).toFixed(2) : '',
      external_price: pricing.external ? (pricing.external / 100).toFixed(2) : '',
    });
    setSelectedSlot(slot);
    setPricingDialogOpen(true);
  };

  // Edit slot handlers
  const openEditDialog = (slot) => {
    // Format datetime for input
    const formatForInput = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toISOString().slice(0, 16);
    };
    
    setEditForm({
      slot_start_at: formatForInput(slot.slot_start_at),
      slot_end_at: formatForInput(slot.slot_end_at),
      category_label: slot.category_label,
      is_active: slot.is_active,
    });
    setSelectedSlot(slot);
    setEditDialogOpen(true);
  };

  const handleEditSlot = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    
    setSaving(true);
    try {
      const updateData = {
        category_label: editForm.category_label,
        is_active: editForm.is_active,
      };
      
      if (editForm.slot_start_at) {
        updateData.slot_start_at = new Date(editForm.slot_start_at).toISOString();
      }
      if (editForm.slot_end_at) {
        updateData.slot_end_at = new Date(editForm.slot_end_at).toISOString();
      }
      
      await adminAPI.updateTicketSlot(selectedSlot.id, updateData);
      toast.success('Slot updated successfully');
      setEditDialogOpen(false);
      setSelectedSlot(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update slot');
    } finally {
      setSaving(false);
    }
  };

  // Delete slot handlers
  const openDeleteDialog = async (slot) => {
    setSelectedSlot(slot);
    setLoadingStats(true);
    setDeleteDialogOpen(true);
    
    try {
      const res = await adminAPI.getTicketSlotStats(slot.id);
      setSlotStats(res.data);
    } catch (error) {
      toast.error('Failed to load slot statistics');
      setSlotStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleDeleteSlot = async () => {
    if (!selectedSlot) return;
    
    setDeleting(true);
    try {
      const res = await adminAPI.deleteTicketSlot(selectedSlot.id);
      toast.success(`Slot deleted. Removed ${res.data.deleted.ticket_files} ticket files and ${res.data.deleted.pricing_rules} pricing rules.`);
      setDeleteDialogOpen(false);
      setSelectedSlot(null);
      setSlotStats(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete slot');
    } finally {
      setDeleting(false);
    }
  };

  const handleClearFiles = async () => {
    if (!selectedSlot) return;
    
    setClearingFiles(true);
    try {
      const res = await adminAPI.deleteSlotTicketFiles(selectedSlot.id);
      toast.success(res.data.message || `Deleted ${res.data.deleted_count} ticket files`);
      setClearFilesDialogOpen(false);
      setSelectedSlot(null);
      setSlotStats(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear ticket files');
    } finally {
      setClearingFiles(false);
    }
  };

  return (
    <AdminLayout title="Ticket Slots" subtitle="Manage ticket inventory and pricing">
      <Tabs defaultValue="slots" className="space-y-4">
        <TabsList>
          <TabsTrigger value="slots" data-testid="tab-slots">Slots</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">Inventory</TabsTrigger>
        </TabsList>

        <TabsContent value="slots">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Ticket Slots</CardTitle>
              <div className="flex items-center gap-4">
                <Select value={filterMonument} onValueChange={setFilterMonument}>
                  <SelectTrigger className="w-48" data-testid="filter-monument">
                    <SelectValue placeholder="Filter by monument" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Monuments</SelectItem>
                    {monuments.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  className="bg-indigo-600 hover:bg-indigo-700" 
                  data-testid="create-slot-btn"
                  onClick={() => setSlotDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Slot
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="spinner" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Monument</TableHead>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Available</TableHead>
                      <TableHead>Member Price</TableHead>
                      <TableHead>External Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {slots.map((slot) => {
                      const pricing = getSlotPricing(slot.id);
                      return (
                        <TableRow key={slot.id} className="table-row-hover" data-testid={`slot-row-${slot.id}`}>
                          <TableCell className="font-medium">{slot.monument_name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              {formatDateTime(slot.slot_start_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{slot.category_label}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Ticket className="w-4 h-4 text-slate-400" />
                              <span className="font-semibold">{slot.remaining_tickets}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {pricing.member ? (
                              <span className="font-medium text-emerald-600">{formatPrice(pricing.member)}</span>
                            ) : (
                              <span className="text-slate-400">Not set</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {pricing.external ? (
                              <span className="font-medium text-blue-600">{formatPrice(pricing.external)}</span>
                            ) : (
                              <span className="text-slate-400">Not set</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={slot.is_active ? 'badge-success' : 'badge-neutral'}>
                              {slot.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openEditDialog(slot)}
                                data-testid={`edit-btn-${slot.id}`}
                                title="Edit slot"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openUploadDialog(slot)}
                                data-testid={`upload-btn-${slot.id}`}
                                title="Upload tickets"
                              >
                                <Upload className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openPricingDialog(slot)}
                                data-testid={`pricing-btn-${slot.id}`}
                                title="Set prices"
                              >
                                <Euro className="w-4 h-4" />
                              </Button>
                              {slot.remaining_tickets > 0 && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                  onClick={() => {
                                    setSelectedSlot(slot);
                                    setClearFilesDialogOpen(true);
                                  }}
                                  data-testid={`clear-files-btn-${slot.id}`}
                                  title="Clear all ticket files"
                                >
                                  <FileX2 className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => openDeleteDialog(slot)}
                                data-testid={`delete-btn-${slot.id}`}
                                title="Delete slot"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {slots.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          No slots found. Create your first slot to start managing tickets.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inventory">
          <Card>
            <CardHeader>
              <CardTitle>Ticket Inventory Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {slots.map(slot => {
                  const pricing = getSlotPricing(slot.id);
                  return (
                    <div key={slot.id} className="p-4 border rounded-xl">
                      <h3 className="font-semibold">{slot.monument_name}</h3>
                      <p className="text-sm text-slate-500">{formatDateTime(slot.slot_start_at)}</p>
                      <p className="text-sm text-slate-500 capitalize">{slot.category_label}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-2xl font-bold">{slot.remaining_tickets}</span>
                        <span className="text-sm text-slate-500">available</span>
                      </div>
                      <div className="mt-2 pt-2 border-t text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Member:</span>
                          <span className="font-medium text-emerald-600">
                            {pricing.member ? formatPrice(pricing.member) : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">External:</span>
                          <span className="font-medium text-blue-600">
                            {pricing.external ? formatPrice(pricing.external) : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Slot Dialog */}
      <Dialog open={slotDialogOpen} onOpenChange={setSlotDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Ticket Slot</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSlot} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Monument</Label>
              <Select 
                value={slotForm.monument_id} 
                onValueChange={(v) => setSlotForm({ ...slotForm, monument_id: v })}
              >
                <SelectTrigger data-testid="slot-monument-select">
                  <SelectValue placeholder="Select monument" />
                </SelectTrigger>
                <SelectContent>
                  {monuments.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="datetime-local"
                  value={slotForm.slot_start_at}
                  onChange={(e) => setSlotForm({ ...slotForm, slot_start_at: e.target.value })}
                  required
                  data-testid="slot-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time (optional)</Label>
                <Input
                  type="datetime-local"
                  value={slotForm.slot_end_at}
                  onChange={(e) => setSlotForm({ ...slotForm, slot_end_at: e.target.value })}
                  data-testid="slot-end-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={slotForm.category_label} 
                onValueChange={(v) => setSlotForm({ ...slotForm, category_label: v })}
              >
                <SelectTrigger data-testid="slot-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="adult">Adult</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="senior">Senior</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setSlotDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-slot-btn">
                Create Slot
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Ticket Files</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadTickets} className="space-y-4 mt-4">
            {selectedSlot && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedSlot.monument_name}</p>
                <p className="text-sm text-slate-500">{formatDateTime(selectedSlot.slot_start_at)} - {selectedSlot.category_label}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Select Files (PDF)</Label>
              <Input
                type="file"
                accept=".pdf"
                multiple
                ref={fileInputRef}
                onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                data-testid="upload-file-input"
              />
              {uploadFiles.length > 0 && (
                <p className="text-sm text-slate-500">{uploadFiles.length} file(s) selected</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-indigo-600 hover:bg-indigo-700" 
                disabled={uploadFiles.length === 0}
                data-testid="submit-upload-btn"
              >
                Upload {uploadFiles.length} Files
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pricing Dialog - Updated to set both prices */}
      <Dialog open={pricingDialogOpen} onOpenChange={setPricingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Ticket Prices</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePricing} className="space-y-4 mt-4">
            {selectedSlot && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedSlot.monument_name}</p>
                <p className="text-sm text-slate-500">
                  {formatDateTime(selectedSlot.slot_start_at)} - {selectedSlot.category_label}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Available tickets: <span className="font-semibold">{selectedSlot.remaining_tickets}</span>
                </p>
              </div>
            )}
            
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-emerald-100 text-emerald-700">Member Price</Badge>
              </div>
              <div className="space-y-2">
                <Label>Price for Members (EUR)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricingForm.member_price}
                    onChange={(e) => setPricingForm({ ...pricingForm, member_price: e.target.value })}
                    placeholder="40.00"
                    className="pl-7"
                    data-testid="member-price-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Price charged to member users when they download</p>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-blue-100 text-blue-700">External Price</Badge>
              </div>
              <div className="space-y-2">
                <Label>Price for External Users (EUR)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricingForm.external_price}
                    onChange={(e) => setPricingForm({ ...pricingForm, external_price: e.target.value })}
                    placeholder="45.00"
                    className="pl-7"
                    data-testid="external-price-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Price charged to external users when they download</p>
              </div>
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <strong>Note:</strong> Both user types download from the same ticket pool. 
              Setting different prices allows you to offer better rates to members.
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setPricingDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-indigo-600 hover:bg-indigo-700" 
                disabled={savingPricing}
                data-testid="submit-pricing-btn"
              >
                {savingPricing ? 'Saving...' : 'Save Prices'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Slot Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Ticket Slot</DialogTitle>
            <DialogDescription>
              Update the slot details. Changes to date/time may affect ticket availability.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSlot} className="space-y-4 mt-4">
            {selectedSlot && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedSlot.monument_name}</p>
                <p className="text-sm text-slate-500">
                  Current: {formatDateTime(selectedSlot.slot_start_at)}
                </p>
                <p className="text-sm text-slate-500">
                  Available tickets: <span className="font-semibold">{selectedSlot.remaining_tickets}</span>
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="datetime-local"
                  value={editForm.slot_start_at}
                  onChange={(e) => setEditForm({ ...editForm, slot_start_at: e.target.value })}
                  required
                  data-testid="edit-slot-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time (optional)</Label>
                <Input
                  type="datetime-local"
                  value={editForm.slot_end_at}
                  onChange={(e) => setEditForm({ ...editForm, slot_end_at: e.target.value })}
                  data-testid="edit-slot-end-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={editForm.category_label} 
                onValueChange={(v) => setEditForm({ ...editForm, category_label: v })}
              >
                <SelectTrigger data-testid="edit-slot-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="adult">Adult</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="senior">Senior</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label>Active Status</Label>
                <p className="text-xs text-slate-500">Inactive slots cannot be used for downloads</p>
              </div>
              <Switch
                checked={editForm.is_active}
                onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                data-testid="edit-slot-active-switch"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-indigo-600 hover:bg-indigo-700"
                disabled={saving}
                data-testid="submit-edit-slot-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Slot Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Ticket Slot
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. Please review the slot details below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedSlot && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedSlot.monument_name}</p>
                <p className="text-sm text-slate-500">{formatDateTime(selectedSlot.slot_start_at)}</p>
                <p className="text-sm text-slate-500 capitalize">{selectedSlot.category_label}</p>
              </div>
            )}
            
            {loadingStats ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              </div>
            ) : slotStats && (
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-900">Slot Statistics</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Total tickets:</span>
                      <span className="ml-2 font-medium">{slotStats.tickets.total}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Available:</span>
                      <span className="ml-2 font-medium text-green-600">{slotStats.tickets.available}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Downloaded:</span>
                      <span className="ml-2 font-medium text-blue-600">{slotStats.tickets.downloaded}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Voided:</span>
                      <span className="ml-2 font-medium text-slate-500">{slotStats.tickets.voided}</span>
                    </div>
                  </div>
                  {slotStats.downloads.count > 0 && (
                    <div className="mt-2 pt-2 border-t border-blue-200">
                      <span className="text-slate-500">Total revenue:</span>
                      <span className="ml-2 font-medium text-green-600">
                        {formatPrice(slotStats.downloads.total_revenue_cents)}
                      </span>
                    </div>
                  )}
                </div>
                
                {!slotStats.can_delete ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-900">Cannot Delete</p>
                        <p className="text-sm text-amber-700">
                          This slot has {slotStats.downloads.count} downloaded ticket(s). 
                          You can deactivate the slot instead to prevent further downloads.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">
                      <strong>Warning:</strong> Deleting this slot will permanently remove:
                    </p>
                    <ul className="text-sm text-red-600 mt-1 ml-4 list-disc">
                      <li>{slotStats.tickets.total} ticket file(s)</li>
                      <li>{slotStats.pricing_rules.length} pricing rule(s)</li>
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setSlotStats(null);
                }}
              >
                Cancel
              </Button>
              {slotStats?.can_delete ? (
                <Button 
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleDeleteSlot}
                  disabled={deleting}
                  data-testid="confirm-delete-slot-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete Slot'}
                </Button>
              ) : (
                <Button 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={async () => {
                    try {
                      await adminAPI.updateTicketSlot(selectedSlot.id, { is_active: false });
                      toast.success('Slot deactivated');
                      setDeleteDialogOpen(false);
                      setSlotStats(null);
                      loadData();
                    } catch (error) {
                      toast.error('Failed to deactivate slot');
                    }
                  }}
                  data-testid="deactivate-slot-btn"
                >
                  Deactivate Instead
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clear Files Dialog */}
      <Dialog open={clearFilesDialogOpen} onOpenChange={setClearFilesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <FileX2 className="w-5 h-5" />
              Clear Ticket Files
            </DialogTitle>
            <DialogDescription>
              This will delete all ticket files for this slot. The slot itself will remain.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedSlot && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedSlot.monument_name}</p>
                <p className="text-sm text-slate-500">{formatDateTime(selectedSlot.slot_start_at)}</p>
                <p className="text-sm text-slate-500 capitalize">{selectedSlot.category_label}</p>
                <p className="text-sm font-medium text-orange-600 mt-2">
                  {selectedSlot.remaining_tickets} available ticket(s) will be deleted
                </p>
              </div>
            )}
            
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-700">
                <strong>Note:</strong> This action cannot be undone. After clearing, you can upload new ticket files.
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setClearFilesDialogOpen(false);
                  setSelectedSlot(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                className="bg-orange-600 hover:bg-orange-700"
                onClick={handleClearFiles}
                disabled={clearingFiles}
                data-testid="confirm-clear-files-btn"
              >
                {clearingFiles ? 'Clearing...' : 'Clear All Files'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminTicketSlots;
